import React, { useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

function VerifyEmail() {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  const email = location.state?.email;

  if (!email) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <h2 className="text-2xl font-bold mb-4">Erro</h2>
              <p className="text-slate-600">Nenhum e-mail foi fornecido. Por favor, <Link to="/register" className="text-primary hover:underline">retorne para a página de cadastro</Link>.</p>
            </div>
        </div>
    );
  }

  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^[0-9]*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (!pastedData) return;

    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
       newCode[i] = pastedData[i];
    }
    setCode(newCode);
    
    // Focus the last filled or the next empty
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex].focus();
  };

  const handleSubmit = async () => {
    const fullCode = code.join('');
    if (fullCode.length < 6) {
       toast.error('Please enter all 6 digits.');
       return;
    }

    const loadingToast = toast.loading('Verifying code...');
    try {
      const response = await api.post('/auth/verify', { email, code: fullCode });
      toast.dismiss(loadingToast);
      toast.success(response.data.message || 'Account verified successfully!');
      navigate('/login');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Error: ' + (error.response?.data?.error || 'Invalid code'));
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    const loadingToast = toast.loading('Resending code...');
    try {
      // Assuming there is a resend endpoint or similar. If not, this is a placeholder.
      // await api.post('/auth/resend-verification', { email });
      // We will simulate it for now if endpoint doesn't exist, to match design request.
      await new Promise(r => setTimeout(r, 1000));
      toast.dismiss(loadingToast);
      toast.success('A new verification code has been sent!');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to resend code.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-inter flex flex-col relative">
      {/* Top Header outside card */}
      <div className="absolute top-0 left-0 right-0 p-8 flex justify-between items-center">
        <div className="flex items-center space-x-2 text-slate-900">
           <span className="material-symbols-outlined text-2xl font-bold">account_balance_wallet</span>
           <span className="text-lg font-bold font-manrope tracking-tight">FinControl</span>
        </div>
        <Link to="#" className="text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors">Support</Link>
      </div>

      {/* Main Content Centered */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
         
         <div className="w-full max-w-[460px] text-center mb-8 pt-10">
            <h1 className="text-3xl font-bold font-manrope text-slate-900 mb-4">Verify your identity</h1>
            <p className="text-sm font-medium text-slate-500 leading-relaxed">
               We've sent a 6-digit security code to<br/>
               <strong className="text-slate-800">{email}</strong>. Please enter it below to<br/>
               secure your account.
            </p>
         </div>

         {/* The Card */}
         <div className="w-full max-w-[460px] bg-white rounded-2xl shadow-xl shadow-slate-200/50 relative overflow-hidden border border-slate-100 mb-8">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#4f46e5]"></div>
            
            <div className="p-8 md:p-10">
               <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">Security Code</label>
               
               {/* 6 Boxes */}
               <div className="flex justify-between space-x-2 md:space-x-4 mb-8">
                  {code.map((digit, idx) => (
                    <input 
                      key={idx}
                      ref={el => inputRefs.current[idx] = el}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      onPaste={idx === 0 ? handlePaste : undefined}
                      className="w-12 h-14 bg-slate-200/60 border-transparent rounded-lg text-center text-xl font-bold text-slate-900 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all placeholder-slate-300"
                      placeholder="•"
                    />
                  ))}
               </div>

               <button onClick={handleSubmit} className="w-full bg-[#4f46e5] text-white font-bold py-3.5 px-4 rounded-xl shadow-[0_8px_16px_-6px_rgba(79,70,229,0.4)] hover:bg-[#4338ca] hover:shadow-[0_12px_20px_-8px_rgba(79,70,229,0.5)] hover:-translate-y-[1px] transition-all duration-200 flex items-center justify-center space-x-2 mb-8">
                  <span>Verify Account</span>
                  <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
               </button>

               <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-start space-x-4">
                  <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                     <span className="material-symbols-outlined text-white text-[14px]">info</span>
                  </div>
                  <p className="text-xs font-medium text-slate-500 leading-relaxed text-left">
                     Check your spam or junk folder if you don't see the email within 2 minutes.
                  </p>
               </div>
            </div>
         </div>

         {/* Bottom Actions */}
         <div className="flex flex-col items-center space-y-6">
            <button onClick={handleResend} disabled={isResending} className="text-sm font-bold text-[#4f46e5] hover:text-[#4338ca] transition-colors disabled:opacity-50">
               {isResending ? 'Sending...' : 'Resend verification code'}
            </button>
            <Link to="/login" className="flex items-center space-x-2 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors">
               <span className="material-symbols-outlined text-[18px]">arrow_back</span>
               <span>Back to login</span>
            </Link>
         </div>
      </div>
    </div>
  );
}

export default VerifyEmail;